import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { Logger } from './helper/app-error-logger';
import { AppConfig } from './helper/app.config';
import { AppStorage } from './helper/app-storage';
import { MyApp } from './helper/app';
import { UiHelper } from './helper/ui-helper';
import { Utils } from './helper/utils';
import { SystemService } from './system.service';
import { RemoteService } from './remote.service';
import { StammdatenService } from './stammdaten.service';
import { AuftragService } from './auftrag.service';
import { UvvService } from './uvv.service';
import { NachrichtService } from './nachricht.service';
import { GlasAnalyseService } from './glassanalyse.service';
import { BerichteService } from './berichte.service';
import { ZeiterfassungService } from './zeiterfassung.service';

const log = new Logger('GeraetResetService');

/**
 * Löscht die lokalen Daten und die Konfiguration der App.
 * Wird sowohl vom Benutzer (Lokale Daten löschen) als auch vom Administrator
 * über das Portal (Fernlöschung) verwendet.
 */
@Injectable({
    providedIn: 'root'
})
export class GeraetResetService {

    /**
     * Läuft gerade ein Zurücksetzen? Verhindert die doppelte Ausführung, wenn der Befehl
     * über beide Kanäle (WebSocket und Push) ankommt.
     */
    private zuruecksetzenLaeuft = false;

    constructor(
        private systemService: SystemService,
        private remoteService: RemoteService,
        private stammdatenService: StammdatenService,
        private auftragService: AuftragService,
        private uvvService: UvvService,
        private nachrichtService: NachrichtService,
        private glasAnalyseService: GlasAnalyseService,
        private berichteService: BerichteService,
        private zeiterfassungService: ZeiterfassungService,
        private router: Router) {
    }

    /**
     * Setzt die App komplett zurück (Fernlöschung durch einen Sysadmin):
     * Alle lokalen Daten und die komplette Konfiguration unter "System" werden gelöscht.
     * Noch nicht gesendete Daten werden dabei bewusst verworfen.
     */
    async geraetZuruecksetzen(): Promise<void> {
        if (this.zuruecksetzenLaeuft) {
            log.warn('geraetZuruecksetzen läuft bereits. Abbruch.');
            return;
        }

        this.zuruecksetzenLaeuft = true;

        log.warn('GERÄT WIRD ZURÜCKGESETZT');

        MyApp.loescheAlleDatenAktiv = true;

        try {
            await UiHelper.showLoading('Das Gerät wird zurückgesetzt...');

            // Noch nicht gesendete Daten werden bewusst verworfen: das Gerät soll restlos leer sein.
            await this.loescheLokaleDaten(true);

            // Vollzug melden, solange der Token noch vorhanden ist. Erst danach verwirft ihn der Server.
            await this.meldeZuruecksetzenFertig();

            await this.loescheKonfiguration();
        } catch (err) {
            log.error('geraetZuruecksetzen: ' + Utils.getErrorMessage(err), err);
        } finally {
            MyApp.loescheAlleDatenAktiv = false;
            this.zuruecksetzenLaeuft = false;

            await UiHelper.hideLoading();
        }

        try {
            await this.router.navigateByUrl('/einstellungen');
        } catch (err) {
            log.error('geraetZuruecksetzen: navigate: ' + Utils.getErrorMessage(err), err);
        }

        await UiHelper.showAlert(
            'Das Gerät wurde vom Administrator zurückgesetzt. Alle lokalen Daten und die Konfiguration wurden gelöscht.',
            'Gerät zurückgesetzt');
    }

    /**
     * Meldet dem Server, dass das Zurücksetzen ausgeführt wurde. Der Server verwirft daraufhin
     * den Login-Token. Schlägt der Aufruf fehl (z.B. keine Verbindung), wird trotzdem weiter
     * gelöscht - der Token ist dann zwar noch gültig, aber auf dem Gerät nicht mehr vorhanden.
     */
    private async meldeZuruecksetzenFertig(): Promise<void> {
        try {
            const result = await this.remoteService.zuruecksetzenFertig();

            if (!result?.success) {
                log.warn('meldeZuruecksetzenFertig: ' + (result?.message || 'keine Antwort vom Server'));
            }
        } catch (err) {
            log.warn('meldeZuruecksetzenFertig: ' + Utils.getErrorMessage(err), err);
        }
    }

    /**
     * Löscht alle lokalen Daten (Aufträge, Stammdaten, Nachrichten, UVV, Dateien).
     *
     * @param alles TRUE = zusätzlich Berichte und Zeiterfassung löschen.
     *              Wird nur beim Zurücksetzen durch den Administrator verwendet.
     */
    async loescheLokaleDaten(alles = false): Promise<void> {
        log.warn('LÖSCHE LOKALE DATEN');

        try {
            await this.stammdatenService.loescheStammdaten();
            await this.auftragService.loescheAlleAuftraege();
            await this.auftragService.loescheAlteAuftragsDateien();
            await this.uvvService.loescheDaten();
            await this.nachrichtService.loescheDaten();
            await this.glasAnalyseService.loescheDaten();
            await this.systemService.loescheAllePostausgangDateien();
            await this.systemService.loescheAlleDateien();

            if (alles) {
                await this.berichteService.loescheDaten();
                await this.zeiterfassungService.loescheDaten();
            }

            await AppStorage.current.set('stammdaten-timestamp', 0);
            await AppStorage.current.set('changelog-timestamp', 0);

            MyApp.current.tourGestartet.next(false);
            MyApp.current.aktuelleTour.next(null);

            await AppStorage.current.loescheAlleDaten();
        } catch (err) {
            log.error('loescheLokaleDaten: ' + Utils.getErrorMessage(err), err);
        }
    }

    /**
     * Löscht die komplette Konfiguration unter "System" - inklusive Host - und leert
     * anschließend den gesamten Speicher.
     *
     * Der Speicher wird bewusst komplett geleert und nicht Schlüssel für Schlüssel abgeräumt:
     * sonst bleiben Reste wie der angemeldete Fahrer, das angemeldete Fahrzeug, der Anhänger
     * oder die aktuelle Tour zurück. Die App ist danach im Zustand einer Neuinstallation.
     */
    async loescheKonfiguration(): Promise<void> {
        log.warn('LÖSCHE KONFIGURATION');

        const appConfig = AppConfig.current;

        appConfig.mandant = null;
        appConfig.geraeteNummer = null;
        appConfig.passwort = null;
        appConfig.schluessel = null;
        appConfig.token = '';
        appConfig.GeraeteBezeichnung = null;
        appConfig.GeraeteGruppe = null;
        appConfig.UnterMandant = null;
        appConfig.Anlagen = [];
        appConfig.aktuelleAnlage = null;
        appConfig.geraetegruppen = [];
        appConfig.gekoppelteDrucker = [];
        appConfig.gekoppelteWaagen = [];
        appConfig.einstellungen = {};
        appConfig.arbeitszeitKonfiguration = null;
        appConfig.stoerzeitKonfiguration = null;
        appConfig.glasAnalyse = null;
        appConfig.setHost(environment.host);

        try {
            await AppStorage.current.clear();
        } catch (err) {
            log.error('loescheKonfiguration: Speicher leeren: ' + Utils.getErrorMessage(err), err);
        }

        try {
            localStorage.clear();
        } catch (err) {
            log.error('loescheKonfiguration: localStorage leeren: ' + Utils.getErrorMessage(err), err);
        }

        AppConfig.changed.next();
        AppConfig.loaded.next(appConfig);

        MyApp.current.refresh();
    }
}
